import type { ProjectPermission, ProjectPermissionCatalogItem, ProjectRole } from './api/projectPermissions';

/**
 * permission 키를 "기능 × 접근 수준"으로 읽는 표시 계층.
 *
 * 서버 모델은 평평한 permission 집합이고 그게 판정 기준이다. 여기서 사다리를 만드는 건 화면 전용이며,
 * 두 가지를 얻으려는 것이다.
 *
 * 1. 체크박스 40여 개 대신 기능당 한 줄로 줄인다.
 * 2. `write`는 있는데 `read`는 없는 것 같은 무의미한 조합을 애초에 만들 수 없게 한다.
 *    built-in 묶음은 전부 누적 관계(read ⊂ write ⊂ manage)라 이 제약은 기존 역할을 하나도 배제하지 않는다.
 *
 * 사다리는 카탈로그에서 만든다. 여기에 기능·액션을 하드코딩하면 서버가 permission을 추가해도
 * 화면에서 고를 수 없어 두 축이 조용히 어긋난다.
 */

/**
 * 액션 강도. 같은 순위(`enter`/`read`)는 기능마다 둘 중 하나만 존재한다.
 * 예외로 `runners.requestPlan`은 `read`와 같은 순위를 공유한다. 안정 정렬이 동점자 순서를
 * 카탈로그 순서대로 유지하므로 사다리는 읽기 → 플랜 요청 → 편집 순이 되고, 단계 선택의
 * 누적 부여(`permissionsUpToLevel`)도 그 순서를 따른다.
 */
const ACTION_RANK: Record<string, number> = {
  enter: 0,
  read: 0,
  requestPlan: 0,
  contribute: 1,
  write: 1,
  manage: 2,
};

export type FeatureLadder = {
  feature: string;
  /** 약한 것부터 강한 것 순. 예: `['read', 'write', 'manage']` */
  actions: string[];
  /** `actions`와 같은 순서의 permission 키. */
  keys: ProjectPermission[];
};

/** 카탈로그를 기능별 사다리로 접는다. 기능 순서는 카탈로그 순서(= 서버 선언 순서)를 따른다. */
export const buildFeatureLadders = (catalog: ProjectPermissionCatalogItem[]): FeatureLadder[] => {
  const byFeature = new Map<string, ProjectPermissionCatalogItem[]>();
  for (const item of catalog) {
    const list = byFeature.get(item.feature) ?? [];
    list.push(item);
    byFeature.set(item.feature, list);
  }

  return [...byFeature.entries()].map(([feature, items]) => {
    const sorted = [...items].sort((a, b) => (ACTION_RANK[a.action] ?? 0) - (ACTION_RANK[b.action] ?? 0));
    return {
      feature,
      actions: sorted.map((item) => item.action),
      keys: sorted.map((item) => item.key),
    };
  });
};

/**
 * 이 기능에서 부여된 가장 강한 단계의 인덱스. 아무것도 없으면 `-1`.
 *
 * 가장 강한 것을 기준으로 삼는 이유: API로 만들어진 역할이 사다리와 어긋난 조합(예: `read` 없이
 * `manage`)을 가질 수 있는데, 그걸 "권한 없음"으로 표시하면 실제보다 약해 보인다.
 */
export const featureLevelIndex = (ladder: FeatureLadder, permissions: ReadonlySet<ProjectPermission>): number => {
  let index = -1;
  ladder.keys.forEach((key, position) => {
    if (permissions.has(key)) index = position;
  });
  return index;
};

/** 해당 단계까지의 누적 permission. `-1`이면 빈 배열. */
export const permissionsUpToLevel = (ladder: FeatureLadder, levelIndex: number): ProjectPermission[] =>
  levelIndex < 0 ? [] : ladder.keys.slice(0, levelIndex + 1);

/**
 * 한 기능의 단계만 교체한다.
 *
 * 손대지 않은 기능의 키는 그대로 둔다 — 열어서 저장하는 것만으로 다른 기능의 조합이 조용히
 * 정규화되면, 화면이 사용자가 하지 않은 변경을 만들어내는 셈이다.
 */
export const withFeatureLevel = (
  permissions: ReadonlySet<ProjectPermission>,
  ladder: FeatureLadder,
  levelIndex: number,
): Set<ProjectPermission> => {
  const next = new Set(permissions);
  for (const key of ladder.keys) next.delete(key);
  for (const key of permissionsUpToLevel(ladder, levelIndex)) next.add(key);
  return next;
};

export type FeatureLevel = { feature: string; action: string };

/** 역할이 실제로 주는 것을 기능별 최고 단계로 접는다(요약·diff 공용). */
export const featureLevels = (ladders: FeatureLadder[], permissions: readonly ProjectPermission[]): FeatureLevel[] => {
  const set = new Set(permissions);
  const levels: FeatureLevel[] = [];
  for (const ladder of ladders) {
    const index = featureLevelIndex(ladder, set);
    if (index >= 0) levels.push({ feature: ladder.feature, action: ladder.actions[index] });
  }
  return levels;
};

/**
 * 역할 요약에서 감출 기능.
 *
 * `project.enter`는 커스텀 역할에 서버가 항상 넣으므로 모든 역할에 똑같이 붙는다. 요약에 실으면
 * 역할끼리 구분되는 정보를 밀어낸다.
 */
const SUMMARY_HIDDEN_FEATURES = new Set(['project']);

export const summaryFeatureLevels = (
  ladders: FeatureLadder[],
  permissions: readonly ProjectPermission[],
): FeatureLevel[] => featureLevels(ladders, permissions).filter((level) => !SUMMARY_HIDDEN_FEATURES.has(level.feature));

export type PermissionDiff = {
  added: FeatureLevel[];
  removed: FeatureLevel[];
  /** 기능은 그대로인데 단계만 바뀐 것. `from`은 이전 단계. */
  changed: (FeatureLevel & { from: string })[];
};

/** 저장 전후를 기능 단계로 비교한다. permission 키를 나열하는 것보다 "무엇이 달라지는가"가 읽힌다. */
export const diffFeatureLevels = (
  ladders: FeatureLadder[],
  before: readonly ProjectPermission[],
  after: readonly ProjectPermission[],
): PermissionDiff => {
  const beforeMap = new Map(featureLevels(ladders, before).map((level) => [level.feature, level.action]));
  const afterMap = new Map(featureLevels(ladders, after).map((level) => [level.feature, level.action]));

  const diff: PermissionDiff = { added: [], removed: [], changed: [] };
  for (const ladder of ladders) {
    const from = beforeMap.get(ladder.feature);
    const to = afterMap.get(ladder.feature);
    if (from === to) continue;
    if (from === undefined && to !== undefined) diff.added.push({ feature: ladder.feature, action: to });
    else if (from !== undefined && to === undefined) diff.removed.push({ feature: ladder.feature, action: from });
    else if (from !== undefined && to !== undefined) diff.changed.push({ feature: ladder.feature, action: to, from });
  }
  return diff;
};

/** 여러 역할의 유효 permission 합집합. 서버 판정과 같은 규칙이다. */
export const unionRolePermissions = (roles: readonly ProjectRole[]): ProjectPermission[] => {
  const union = new Set<ProjectPermission>();
  for (const role of roles) {
    for (const permission of role.permissions) union.add(permission);
  }
  return [...union];
};
